import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";
import * as React from "react";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Check if an element is inside the Sonner toast container.
 * Used by capture-phase event handler to stop propagation before Radix processes it.
 */
function isElementInsideToast(element: EventTarget | null): boolean {
  if (!element) return false;
  
  // Handle non-Element targets (Text nodes, SVG elements, etc.)
  let el: Element | null = null;
  if (element instanceof Element) {
    el = element;
  } else if (element instanceof Node) {
    el = element.parentElement;
  }
  
  if (!el) return false;
  
  // Check using closest() for data attributes
  const TOAST_SELECTORS = '[data-sonner-toaster], [data-sonner-toast]';
  if (el.closest(TOAST_SELECTORS)) {
    return true;
  }
  
  // Fallback: check if toast container contains the element
  const toastContainer = document.querySelector('[data-sonner-toaster]');
  if (toastContainer && toastContainer.contains(el)) {
    return true;
  }
  
  return false;
}

/**
 * Sonner Toaster with BPM-style UX:
 * - Close button hidden by default, visible on hover
 * - Max z-index (2147483647) to appear above all dialogs/overlays
 * - Capture-phase event handler to stop propagation before Radix can dismiss dialogs
 * - Consistent styling with Radix toast
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  // CRITICAL: Capture-phase handler to stop toast clicks from reaching Radix's outside-click detector
  // This runs BEFORE Radix's pointerdown listener and prevents dialog dismissal
  React.useEffect(() => {
    const handlePointerDownCapture = (event: PointerEvent) => {
      if (isElementInsideToast(event.target)) {
        // Stop the event from propagating to Radix's outside-click handler
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    };

    const handleMouseDownCapture = (event: MouseEvent) => {
      if (isElementInsideToast(event.target)) {
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    };

    // Add listeners in capture phase (third param = true)
    document.addEventListener('pointerdown', handlePointerDownCapture, true);
    document.addEventListener('mousedown', handleMouseDownCapture, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDownCapture, true);
      document.removeEventListener('mousedown', handleMouseDownCapture, true);
    };
  }, []);

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      closeButton
      // z-index set via CSS in index.css for !important override
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg group-[.toaster]:rounded-lg group-[.toaster]:pr-8",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          // Close button: match BPM/Radix toast exactly - hidden by default, visible on group hover
          closeButton:
            "!absolute !right-2 !top-2 !left-auto !transform-none !opacity-0 group-hover:!opacity-100 !transition-opacity !bg-transparent !border-0 !p-1 !text-foreground/50 hover:!text-foreground !rounded-md !pointer-events-auto",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
